import { useState, useEffect } from "react";
import API from "@/lib/api";
import { formatCurrency, formatPercent } from "@/lib/formatters";
import MetricCard from "@/components/shared/MetricCard";
import DataTable from "@/components/shared/DataTable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, DollarSign, TrendingUp, Percent, Plus, Upload, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import AddEditDialog from "@/components/shared/AddEditDialog";
import UploadDialog from "@/components/shared/UploadDialog";
import DeleteConfirmDialog from "@/components/shared/DeleteConfirmDialog";

const SECTORS = ["All", "Industrial", "Multifamily", "Office", "Retail", "Hospitality", "Data Centers", "Healthcare", "Student Housing"];

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectorFilter, setSectorFilter] = useState("All");
  const [showAdd, setShowAdd] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    const params = {};
    if (sectorFilter !== "All") params.sector = sectorFilter;
    API.get("/properties", { params })
      .then((res) => setProperties(res.data.properties || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [sectorFilter, refreshKey]);

  const avgNoi = properties.length ? properties.reduce((a, p) => a + p.noi, 0) / properties.length : 0;
  const avgOcc = properties.length ? properties.reduce((a, p) => a + p.occupancy_rate, 0) / properties.length : 0;
  const avgCap = properties.length ? properties.reduce((a, p) => a + p.cap_rate, 0) / properties.length : 0;
  const totalVal = properties.reduce((a, p) => a + p.value, 0);

  const columns = [
    { key: "name", label: "Property", sortable: false, render: (v) => <span className="font-medium text-foreground">{v}</span> },
    { key: "city", label: "City" },
    { key: "state_code", label: "State" },
    { key: "sector", label: "Sector", render: (v) => <Badge variant="secondary" className="font-data text-xs">{v}</Badge> },
    { key: "value", label: "Value", align: "right", render: (v) => formatCurrency(v) },
    { key: "noi", label: "NOI", align: "right", render: (v) => formatCurrency(v) },
    { key: "occupancy_rate", label: "Occupancy", align: "right", render: (v) => <span className={v >= 95 ? "text-emerald-500" : v >= 90 ? "text-foreground" : "text-amber-500"}>{v}%</span> },
    { key: "cap_rate", label: "Cap Rate", align: "right", render: (v) => `${v}%` },
    { key: "rental_growth", label: "Rent Growth", align: "right", render: (v) => <span className="text-emerald-500">+{v}%</span> },
    { key: "ltv", label: "LTV", align: "right", render: (v) => <span className={v >= 45 ? "text-amber-500" : "text-foreground"}>{v}%</span> },
    { key: "actions", label: "", sortable: false, render: (_, row) => (
      <div className="flex gap-1">
        <button className="p-1 rounded-sm hover:bg-secondary transition-colors" onClick={(e) => { e.stopPropagation(); setEditItem(row); }} data-testid={`edit-${row.id}`}><Pencil size={14} className="text-muted-foreground hover:text-foreground" /></button>
        <button className="p-1 rounded-sm hover:bg-destructive/20 transition-colors" onClick={(e) => { e.stopPropagation(); setDeleteItem(row); }} data-testid={`delete-${row.id}`}><Trash2 size={14} className="text-muted-foreground hover:text-red-500" /></button>
      </div>
    )},
  ];

  if (loading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="properties-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Property Performance</h1>
          <p className="text-sm text-muted-foreground mt-1">Individual asset metrics and analytics</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger className="w-48 bg-card border-border" data-testid="sector-filter">
              <SelectValue placeholder="Filter by Sector" />
            </SelectTrigger>
            <SelectContent>
              {SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="add-property-btn" className="gap-1"><Plus size={14} />Add</Button>
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(true)} data-testid="upload-property-btn" className="gap-1"><Upload size={14} />Upload</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Total Value" value={formatCurrency(totalVal)} icon={DollarSign} />
        <MetricCard label="Avg NOI" value={formatCurrency(avgNoi)} icon={Building2} />
        <MetricCard label="Avg Occupancy" value={formatPercent(avgOcc)} icon={TrendingUp} />
        <MetricCard label="Avg Cap Rate" value={formatPercent(avgCap)} icon={Percent} />
      </div>

      <Card className="border border-border bg-card">
        <CardHeader className="border-b border-border p-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">All Properties</CardTitle>
            <span className="text-xs text-muted-foreground font-data">{properties.length} assets</span>
          </div>
        </CardHeader>
        <CardContent className="p-0" data-testid="properties-table">
          <DataTable columns={columns} data={properties} />
        </CardContent>
      </Card>
      <AddEditDialog open={showAdd || !!editItem} item={editItem} entityType="properties" onClose={() => { setShowAdd(false); setEditItem(null); }} onSave={refresh} />
      <UploadDialog open={showUpload} entityType="properties" onClose={() => setShowUpload(false)} onSuccess={refresh} />
      <DeleteConfirmDialog open={!!deleteItem} item={deleteItem} entityType="properties" onClose={() => setDeleteItem(null)} onDelete={refresh} />
    </div>
  );
}
